import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Play, Pause, Edit, Eye, MousePointer, Target, DollarSign, TrendingUp, Clock, AlertTriangle } from "lucide-react";
import { format, differenceInDays, differenceInHours } from "date-fns";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from "recharts";

const statusColors = {
  DRAFT: "status-draft",
  ACTIVE: "status-active",
  PAUSED: "status-paused",
  COMPLETED: "status-completed"
};

// Pause countdown component for Gold campaigns
function PauseCountdown({ pausedAt, maxPauseDays }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0 });
  const [isExpired, setIsExpired] = useState(false);
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const pauseDate = new Date(pausedAt);
      const expiryDate = new Date(pauseDate.getTime() + maxPauseDays * 24 * 60 * 60 * 1000);
      const now = new Date();
      
      if (now >= expiryDate) {
        setIsExpired(true);
        setTimeLeft({ days: 0, hours: 0 });
      } else {
        const daysLeft = differenceInDays(expiryDate, now);
        const hoursLeft = differenceInHours(expiryDate, now) % 24;
        setTimeLeft({ days: daysLeft, hours: hoursLeft });
        setIsExpired(false);
      }
    };
    
    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [pausedAt, maxPauseDays]);
  
  if (isExpired) {
    return (
      <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          <span className="text-sm font-medium text-red-500">Pause period expired</span>
        </div>
        <p className="text-xs text-red-400 mt-1">
          Campaign must be resumed or it may be auto-cancelled.
        </p>
      </div>
    );
  }
  
  return (
    <div className="mt-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-yellow-500" />
        <span className="text-sm font-medium text-yellow-500">Pause time remaining</span>
      </div>
      <p className="text-2xl font-bold text-yellow-500 mt-1">
        {timeLeft.days}d {timeLeft.hours}h
      </p>
      <p className="text-xs text-yellow-400 mt-1">
        Gold subscription includes {maxPauseDays} days of pause time
      </p>
    </div>
  );
}

export default function CampaignDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCampaignData();
  }, [id]);

  const fetchCampaignData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [campaignRes, analyticsRes, creativesRes] = await Promise.all([
        axios.get(`${API}/campaigns/${id}`, { headers }),
        axios.get(`${API}/analytics/campaigns/${id}`, { headers }),
        axios.get(`${API}/creatives`, { headers })
      ]);
      setCampaign(campaignRes.data);
      setAnalytics(analyticsRes.data);
      setCreatives(creativesRes.data.filter(c => campaignRes.data.creative_ids.includes(c.id)));
    } catch (error) {
      toast.error("Failed to fetch campaign data");
      navigate("/campaigns");
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    try {
      await axios.post(`${API}/campaigns/${id}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Campaign activated");
      fetchCampaignData();
    } catch (error) {
      toast.error("Failed to activate campaign");
    }
  };

  const handlePause = async () => {
    try {
      await axios.post(`${API}/campaigns/${id}/pause`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Campaign paused");
      fetchCampaignData();
    } catch (error) {
      toast.error("Failed to pause campaign");
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="glass-heavy rounded-lg p-3 border border-border shadow-xl">
          <p className="text-sm font-medium text-foreground">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-xs" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-primary">Loading campaign...</div>
      </div>
    );
  }

  if (!campaign) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="campaign-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/campaigns")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-black tracking-tight">{campaign.name}</h1>
              <Badge className={statusColors[campaign.status]}>
                {campaign.status}
              </Badge>
            </div>
            <p className="text-muted-foreground mt-1">{campaign.description || "No description"}</p>
            
            {/* Pause Countdown for Gold campaigns */}
            {campaign.status === "PAUSED" && campaign.paused_at && campaign.tier === "GOLD" && (
              <PauseCountdown pausedAt={campaign.paused_at} maxPauseDays={campaign.max_pause_days || 30} />
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {campaign.status === "DRAFT" || campaign.status === "PAUSED" ? (
            <Button onClick={handleActivate} data-testid="activate-campaign-btn">
              <Play className="mr-2 h-4 w-4" />
              Activate
            </Button>
          ) : campaign.status === "ACTIVE" ? (
            <Button variant="outline" onClick={handlePause} data-testid="pause-campaign-btn">
              <Pause className="mr-2 h-4 w-4" />
              Pause
            </Button>
          ) : null}
        </div>
      </div>

      {/* Analytics Summary */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Impressions</p>
                  <p className="text-2xl font-bold mt-1">{analytics.summary.impressions?.toLocaleString()}</p>
                </div>
                <div className="p-2 rounded-lg bg-primary/10">
                  <Eye className="h-5 w-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Clicks</p>
                  <p className="text-2xl font-bold mt-1">{analytics.summary.clicks?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">CTR: {analytics.summary.ctr}%</p>
                </div>
                <div className="p-2 rounded-lg bg-green-500/10">
                  <MousePointer className="h-5 w-5 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Delivered Customers</p>
                  <p className="text-2xl font-bold mt-1">{analytics.summary.cpdc?.toLocaleString()}</p>
                </div>
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <Target className="h-5 w-5 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Spend</p>
                  <p className="text-2xl font-bold mt-1">${analytics.summary.spend?.toLocaleString()}</p>
                  <p className="text-xs text-green-500 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    ROI: {analytics.summary.roi}%
                  </p>
                </div>
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <DollarSign className="h-5 w-5 text-yellow-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Campaign Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>Campaign Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between py-2 border-b border-border">
              <span className="text-muted-foreground">Surfaces</span>
              <div className="flex gap-1">
                {campaign.surfaces.map(s => (
                  <Badge key={s} variant="outline" className="text-xs">
                    {s.replace("_", " ")}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="flex justify-between py-2 border-b border-border">
              <span className="text-muted-foreground">Campaign Budget</span>
              <span className="font-mono">{campaign.budget_limit ? `$${campaign.budget_limit}` : "No limit"}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-border">
              <span className="text-muted-foreground">Start Date</span>
              <span>{campaign.start_at ? format(new Date(campaign.start_at), "MMM d, yyyy") : "Not set"}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-border">
              <span className="text-muted-foreground">End Date</span>
              <span>{campaign.end_at ? format(new Date(campaign.end_at), "MMM d, yyyy") : "Not set"}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Created</span>
              <span>{format(new Date(campaign.created_at), "MMM d, yyyy h:mm a")}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>Pricing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between py-2 border-b border-border">
              <span className="text-muted-foreground">Views Rate</span>
              <span className="font-mono font-semibold">${campaign.pricing.cpm_rate}/1k</span>
            </div>
            {campaign.pricing.cpc_enabled && (
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Clicks Rate</span>
                <span className="font-mono">${campaign.pricing.cpc_rate}/click</span>
              </div>
            )}
            {campaign.pricing.cpe_enabled && (
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Engagement Rate</span>
                <span className="font-mono">${campaign.pricing.cpe_rate}</span>
              </div>
            )}
            {campaign.pricing.cpdc_enabled && (
              <div className="flex justify-between py-2">
                <span className="text-muted-foreground">Delivered Customer Rate</span>
                <span className="font-mono">${campaign.pricing.cpdc_rate}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Creatives */}
      {creatives.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>Creatives</CardTitle>
            <CardDescription>{creatives.length} creative(s) in this campaign</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {creatives.map(creative => (
                <div key={creative.id} className="rounded-lg border border-border overflow-hidden">
                  <img
                    src={creative.file_url}
                    alt={creative.name}
                    className="w-full h-32 object-cover"
                    onError={(e) => { e.target.style.background = '#27272A'; e.target.alt = 'No Preview'; }}
                  />
                  <div className="p-2">
                    <p className="text-sm font-medium truncate">{creative.name}</p>
                    <p className="text-xs text-muted-foreground">{creative.type}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
